import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import axios from 'axios';
import { 
  Box, Typography, Paper, Button, Alert, CircularProgress, Divider, 
  Modal, TextField
} from '@mui/material';
import GenericForm from '../components/GenericForm.js';
import { userFormFields as staticUserFields } from '../forms/userFormFields.js';
import { businessFormFields as staticBusinessFields } from '../forms/businessFormFields.js';
import * as courseService from '../services/courseService.js';

// Stile per la finestra modale
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 400,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
};

const Profile = () => {
  const { user, fetchProfile } = useAuth();
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dynamicFormFields, setDynamicFormFields] = useState([]);

  // --- NUOVO STATO PER IL MODALE DI CAMBIO EMAIL ---
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailFormData, setEmailFormData] = useState({ newEmail: '', currentPassword: '' });
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');

  useEffect(() => {
    const loadProfileData = async () => {
      setLoading(true);
      try {
        const [profileRes, categoriesRes, coursesRes] = await Promise.all([
          axios.get('/api/users/profile'),
          axios.get('/api/categories'),
          user.role === 'user' ? courseService.getAllCourses() : Promise.resolve([])
        ]);

        const profileData = profileRes.data;
        const allCategories = categoriesRes.data;
        const allCourses = coursesRes;

        if (profileData.categories) {
          profileData.categories = profileData.categories.map(c => c.category);
        }
        if (profileData.courses) {
          profileData.courses = profileData.courses.map(c => c.course);
        }
        setFormData(profileData);
        
        const baseFields = user.role === 'user' ? staticUserFields : staticBusinessFields;
        const updatedFields = baseFields.map(field => {
            if (field.name === 'categories') {
                return { ...field, options: allCategories };
            }
            if (field.name === 'courses') {
                return { ...field, options: allCourses };
            }
            return field;
        });
        setDynamicFormFields(updatedFields);

      } catch (err) {
        setError('Impossibile caricare i dati del profilo.');
      } finally {
        setLoading(false);
      }
    };
    loadProfileData();
  }, [user.role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const payload = {};
    const editableFields = dynamicFormFields.filter(f => f.name !== 'email' && f.name !== 'accessCode');
    
    editableFields.forEach(field => {
      if (formData.hasOwnProperty(field.name)) {
        payload[field.name] = formData[field.name];
      }
    });

    if (payload.categories && Array.isArray(payload.categories)) {
      payload.categories = payload.categories.map(cat => cat.id);
    }
    if (payload.courses && Array.isArray(payload.courses)) {
      payload.courses = payload.courses.map(course => course.id);
    }

    try {
      await axios.put('/api/users/profile', payload);
      setSuccess('Profilo aggiornato con successo!');
      fetchProfile();
    } catch (err) {
      setError(err.response?.data?.error || 'Si è verificato un errore.');
    }
  };

  // --- NUOVA FUNZIONE PER GESTIRE LA MODIFICA DELL'EMAIL ---
  const handleChangeEmail = async (e) => {
    e.preventDefault();
    setEmailError('');
    setEmailSuccess('');
    try {
      const res = await axios.put('/api/users/change-email', emailFormData);
      setEmailSuccess(res.data.message || 'Email aggiornata!');
      // Aggiorniamo l'email visualizzata nella pagina e chiudiamo il modale dopo un breve ritardo
      setFormData(prev => ({ ...prev, email: emailFormData.newEmail }));
      setTimeout(() => {
        setIsEmailModalOpen(false);
        setEmailFormData({ newEmail: '', currentPassword: '' });
      }, 2000);
    } catch (err) {
      setEmailError(err.response?.data?.error || 'Si è verificato un errore.');
    }
  };

  if (loading || !formData) return <CircularProgress />;

  const displayFields = dynamicFormFields.filter(field => field.name !== 'email' && field.name !== 'accessCode');

  return (
    <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>Modifica Profilo</Typography>
      <Box component="form" onSubmit={handleSubmit}>
            <GenericForm
              fields={displayFields}
              formData={formData}
              setFormData={setFormData}
            />
            {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mt: 3 }}>{success}</Alert>}
            <Button type="submit" variant="contained" size="large" sx={{ mt: 3 }}>
              Salva Modifiche
            </Button>
          </Box>

          {/* --- SEZIONE MODIFICA EMAIL AGGIORNATA --- */}
          <Divider sx={{ my: 4 }} />
          <Box>
            <Typography variant="h5" gutterBottom>Impostazioni Account</Typography>
            <Typography><strong>Email:</strong> {formData.email}</Typography>
            <Button variant="outlined" sx={{ mt: 2 }} onClick={() => setIsEmailModalOpen(true)}>
                Modifica Email
            </Button>
          </Box>

      {/* --- MODALE PER IL CAMBIO EMAIL --- */}
      <Modal open={isEmailModalOpen} onClose={() => setIsEmailModalOpen(false)}>
        <Box sx={modalStyle} component="form" onSubmit={handleChangeEmail}>
            <Typography variant="h6" component="h2">Modifica il tuo indirizzo email</Typography>
            <Typography sx={{ mt: 1, mb: 2 }} color="text.secondary">
                Per sicurezza, inserisci la tua password attuale.
            </Typography>
            <TextField
                fullWidth
                margin="normal"
                label="Nuovo indirizzo email"
                type="email"
                value={emailFormData.newEmail}
                onChange={(e) => setEmailFormData(prev => ({ ...prev, newEmail: e.target.value }))}
                required
            />
            <TextField
                fullWidth
                margin="normal"
                label="Password attuale"
                type="password"
                value={emailFormData.currentPassword}
                onChange={(e) => setEmailFormData(prev => ({ ...prev, currentPassword: e.target.value }))}
                required
            />
            {emailError && <Alert severity="error" sx={{ mt: 2 }}>{emailError}</Alert>}
            {emailSuccess && <Alert severity="success" sx={{ mt: 2 }}>{emailSuccess}</Alert>}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button onClick={() => setIsEmailModalOpen(false)}>Annulla</Button>
                <Button type="submit" variant="contained">Salva Email</Button>
            </Box>
        </Box>
      </Modal>
    </Paper>
  );
};

export default Profile;